/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import TruncatedSeq from "../../../../components/TruncatedSeq";
import { getVariantResult } from "../../../../state/selectors/general.selectors";
import HelperText from "../../../../components/atoms/HelperText";

const EquivalentIndels = ({ variant }) => {
  const values = variant;

  if (
    !values.equivalent ||
    !(values.equivalent.slide_left || values.equivalent.slide_right)
  )
    return null;

  const {
    sequence,
    length: sequenceLength,
    start: sequenceStartPosition,
  } = values.ref_seq;
  const { pos: variantPosition } = values;
  const { slide_left: sl, slide_right: sr } = values.equivalent;
  const refLength = values.ref ? values.ref.length : 0;
  const altLength = values.alt ? values.alt.length : 0;
  const indelType = refLength && !altLength ? "deletion" : "insertion";
  const seqLength = refLength || altLength;
  const slTrunc = Math.min(sl, 50);
  const srTrunc = Math.min(sr, 50 - sl);
  let slideLeftOutOfRange = false;
  if (
    sl > variantPosition - sequenceStartPosition ||
    sl === variantPosition - sequenceStartPosition
  ) {
    slideLeftOutOfRange = true;
  }
  let slideRightOutOfRange = false;
  if (
    sr > sequenceStartPosition + sequenceLength - variantPosition ||
    sr === sequenceStartPosition + sequenceLength - variantPosition
  ) {
    slideRightOutOfRange = true;
  }

  const p1 = variantPosition - sl;
  const p2 = variantPosition + sr - 1;

  const breakpoint1 = variantPosition - sequenceStartPosition - sl;
  const breakpoint2 = variantPosition - sequenceStartPosition + refLength + sr;

  let before = sequence.slice(0, breakpoint1);
  let after = sequence.slice(breakpoint2);

  // Make both before and after equal in length
  const minLength = Math.min(before.length, after.length, 5);
  before = before.slice(before.length - minLength);
  after = after.slice(0, minLength);

  let middle;
  if (indelType === "deletion") {
    middle = sequence.slice(breakpoint1, breakpoint2);
  } else {
    middle =
      sequence.slice(breakpoint1, breakpoint1 + sl) +
      values.alt +
      sequence.slice(breakpoint2 - sr, breakpoint2);
  }

  if (!slideLeftOutOfRange && !slideRightOutOfRange) {
    const indels = [];
    for (let i = 0; i < slTrunc; i++) {
      const middleSequence = middle.slice(i, seqLength + i);
      const beforeHighlightSequence = middle.slice(0, i);
      const afterHighlightSequence = middle.slice(seqLength + i);
      indels.push({
        spec: (
          <span className="tw-mr-8">
            {`${values.chromosome}-${variantPosition - sl + i}-`}
            <span>
              {indelType === "insertion" ? `-${values.ref}` : ``}
              <TruncatedSeq seq={middleSequence} displayWhenEmpty="-" />
              {indelType === "deletion" ? `-${values.alt}` : ``}
            </span>
          </span>
        ),
        beforeHighlight: beforeHighlightSequence,
        middle: <TruncatedSeq seq={middleSequence} displayWhenEmpty="-" />,
        afterHighlight: afterHighlightSequence,
      });
    }
    if (slTrunc && slTrunc < values.equivalent.slide_left) {
      indels.push("...");
    }

    for (let i = 1; i <= srTrunc; i++) {
      const middleSequence = middle.slice(sl + i, sl + seqLength + i);
      const beforeHighlightSequence = middle.slice(0, sl + i);
      const afterHighlightSequence = middle.slice(sl + seqLength + i);
      indels.push({
        spec: (
          <span className="tw-mr-8">
            {`${values.chromosome}-${variantPosition - sl + i}-`}
            <span>
              {indelType === "insertion" ? `-${values.ref}` : ``}
              <TruncatedSeq seq={middleSequence} displayWhenEmpty="-" />
              {indelType === "deletion" ? `-${values.alt}` : ``}
            </span>
          </span>
        ),
        beforeHighlight: beforeHighlightSequence,
        middle: <TruncatedSeq seq={middleSequence} displayWhenEmpty="-" />,
        afterHighlight: afterHighlightSequence,
      });
    }
    if (srTrunc && srTrunc < values.equivalent.slide_right) {
      indels.push("...");
    }

    return (
      <div className="varsome-inner-container varsome-equivalent-indels">
        <div className="varsome-small-subtitle">
          <span>
            Equivalent {`${indelType}s`}
            <HelperText fieldDescriptionKey="Equivalent indels" /> ({sl + sr})
          </span>
        </div>
        {indels.map((indel, i) => {
          const key = `indel-${i}`;
          if (indel === "...") {
            return "...";
          }
          return (
            <div
              className="varsome-equivalent-indel varsome-clearfix"
              key={key}
            >
              <div style={{ display: "block" }}>{indel.spec}</div>
              <div className="indel-seq-container">
                <div className="before">{before}</div>
                <div className="before-highlight">{indel.beforeHighlight}</div>
                <div className={`middle ${indelType}`}>{indel.middle}</div>
                <div className="after-highlight">{indel.afterHighlight}</div>
                <div className="after">{after}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  const indelTypeText = indelType === "deletion" ? " starting from " : " in";
  return (
    <div className="varsome-inner-container varsome-equivalent-indels">
      <div className="varsome-small-subtitle">
        <span>
          Equivalent {`${indelType}s`}
          <HelperText fieldDescriptionKey="Equivalent indels" /> ({sl + sr})
        </span>
      </div>
      <div>
        {`There are ${
          sl + sr
        } equivalent ${indelType}s ${indelTypeText} positions ${p1} to ${p2}`}
      </div>
    </div>
  );
};

EquivalentIndels.propTypes = {
  variant: PropTypes.shape({}),
};

EquivalentIndels.defaultProps = {
  variant: null,
};

const mapStateToProps = (state) => ({
  variant: getVariantResult(state),
});

export default connect(mapStateToProps)(EquivalentIndels);
